import { Box, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import PlansAndBillingService from 'src/services/plans_and_billing';
import { UsageData, PlansAndBillingProps } from 'src/types/billing';
import { ModalPlanOnboarding } from './ModalConfirmUpgrade';
import { BillingCards } from './components/BillingCards';
import { ChoosePlanCards } from './components/ChoosePlanCards';
import { EnterpriseContactCard } from './components/EnterpriseContactCard';

export const PlansAndBilling = ({
  usageData: propUsageData,
  loading: propLoading = false,
  error: propError = null,
  onRetry,
}: PlansAndBillingProps = {}) => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  console.log('PlansAndBilling component rendered');

  const [openPlanOnboarding, setOpenPlanOnboarding] = useState(false);
  const [planOnboardingType, setPlanOnboardingType] = useState<
    'upgrade' | 'downgrade' | 'unlock' | 'buy-extra-credits'
  >('upgrade');

  // Local state for when data is not provided via props
  const [localUsageData, setLocalUsageData] = useState<UsageData | null>(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Use props if provided, otherwise use local state
  const usageData =
    propUsageData !== undefined ? propUsageData : localUsageData;
  const loading = propLoading || localLoading;
  const error = propError || localError;

  // Fetch usage data if not provided via props
  useEffect(() => {
    if (propUsageData === undefined && !localUsageData && !localLoading) {
      fetchUsageData();
    }
  }, [propUsageData, localUsageData, localLoading]);

  const fetchUsageData = async () => {
    try {
      setLocalLoading(true);
      setLocalError(null);
      const response = await PlansAndBillingService.getCurrentUsage();
      setLocalUsageData(response);
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setLocalError('Failed to load usage information');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      fetchUsageData();
    }
  };

  return (
    <Box
      sx={{
        background: darkMode ? theme.palette.background.default : '#fff',
        color: darkMode ? theme.palette.text.primary : '#000',
        flex: 1,
        minHeight: '100vh',
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 4, sm: 5, md: 7 },
        pt: 4,
        transition: 'all 0.3s ease',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          color: darkMode ? theme.palette.text.primary : '#000',
          fontSize: { xs: '24px', sm: '26px', md: '28px' },
          fontWeight: 700,
          fontFamily: 'Power Grotesk',
          textAlign: {
            xs: 'center',
            sm: 'left',
          },
        }}
      >
        Plans & Billing
      </Typography>
      <Typography
        sx={{
          color: darkMode ? theme.palette.text.secondary : '#000',
          fontSize: { xs: '14px', sm: '15px', md: '16px' },
          fontWeight: 400,
          fontFamily: 'Power Grotesk',
          mt: 2,
          mb: 3,
          textAlign: {
            xs: 'center',
            sm: 'left',
          },
        }}
      >
        Get full access to all features. Manage your plan and billing details.
      </Typography>
      <Box>
        <BillingCards
          onUpgrade={() => setOpenPlanOnboarding(true)}
          usageData={usageData}
          loading={loading}
          error={error}
          onRetry={handleRetry}
        />
        <Box sx={{ mt: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: darkMode ? theme.palette.text.primary : '#000',
              fontSize: { xs: '20px', sm: '22px', md: '24px' },
              fontWeight: 700,
              fontFamily: 'Power Grotesk',
              mb: 3,
              textAlign: {
                xs: 'center',
                sm: 'left',
              },
            }}
          >
            Available Plans
          </Typography>
          <ChoosePlanCards disableRedirect={true} />
        </Box>
        <EnterpriseContactCard />
        <ModalPlanOnboarding
          type={planOnboardingType}
          open={openPlanOnboarding}
          onClose={() => setOpenPlanOnboarding(false)}
          onConfirm={async () => {
            console.log('Plan onboarding confirmed:', planOnboardingType);
            // Simular uma operação assíncrona
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }}
        />
      </Box>
    </Box>
  );
};
